import { useMutation } from '@apollo/client/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { round } from 'lodash-es';
import Link from 'next/link';
import { memo, useContext, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Brand } from '@calo/types';

import ActionButtons from './ActionButtons';
import ActualCookedEntriesModal from './ActualCookedEntriesModal';
import { AppContext } from '../../../../App';
import BrandIndicator from '../../../../components/BrandIndicator';
import Modal from '../../../../components/Modal';
import {
  CombinedShortages,
  ComponentWeightBuffer,
  CookingStationName,
  FoodComponent,
  FoodComponentStationTitle
} from '../../../../libs';
import { UPDATE_ACTUAL_COOKED_WEIGHT_V2_MUTATION, VERIFY_COMPONENT_V2_MUTATION } from '../../../../libs/graphQL';
import { getUserAttributes } from '../../../../libs/utils';
import { ComponentService } from '../../../../libs/utils/componentService';
import { getActualCookedWeightStation } from '../../../../libs/utils/foodComponentHelper';
import { calculateWeightChange } from '../../../../libs/utils/weightChangeHelper';
import { ViewTypes } from '../../ComponentsCheck';
import { calculateBufferPercentage, getFirstUnfinishedStation } from '../../helpers';

const NO_SELECTION = new Set<string>();

const STICKY_CHECKBOX_WIDTH = 40;
const STICKY_NAME_WIDTH = 220;
const STICKY_STATIONS_WIDTH = 140;

interface SectionTableRowProps {
  statusAttributes: {
    isCheckable: boolean;
    lastUncheckedStation: string;
  };
  tableName: string;
  isChecked: boolean;
  foodComponent: FoodComponent;
  combinedShortages?: CombinedShortages;
  highlight?: boolean;
  onHighlightEnd?: () => void;
  showAllUnits: boolean;
  selectedHours: string;
  selectedIdsSet?: Set<string>;
  onComponentSelection?: (component: FoodComponent, isSelected: boolean) => void;
  fixFirstTwoColumns?: boolean;
  hasCheckboxColumn?: boolean;
}

const SectionTableRow = ({
  showAllUnits,
  statusAttributes,
  foodComponent,
  tableName,
  combinedShortages,
  isChecked,
  highlight = false,
  selectedHours,
  selectedIdsSet = NO_SELECTION,
  onComponentSelection,
  fixFirstTwoColumns = false,
  hasCheckboxColumn = false
}: SectionTableRowProps) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [isCheckedState, setIsCheckedState] = useState(isChecked);
  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState(false);

  const [mutateFoodComponent, { loading }] = useMutation(VERIFY_COMPONENT_V2_MUTATION);
  const [updateActualCookedWeight, { loading: updatingActual }] = useMutation(UPDATE_ACTUAL_COOKED_WEIGHT_V2_MUTATION);
  const theme = useTheme();
  // const tableAttributes = getTableAttributes(statusAttributes.lastUncheckedStation, theme);

  const currentStation =
    tableName === (ViewTypes.OVERVIEW as unknown as CookingStationName)
      ? getFirstUnfinishedStation(foodComponent)
      : foodComponent.cookingStations?.find((station) => station.name === tableName);
  const isLastStation = foodComponent.cookingStations?.[foodComponent.cookingStations?.length - 1] === currentStation;
  const appContext = useContext(AppContext);

  const weightChangeData = useMemo(
    () => calculateWeightChange(foodComponent.currentCookedWeight?.hourlyData, selectedHours, foodComponent.totalCookedWeight),
    [foodComponent.currentCookedWeight?.hourlyData, selectedHours, foodComponent.totalCookedWeight]
  );

  const handleFoodComponentVerify = async (isVerified: boolean) => {
    const user = getUserAttributes(appContext.user);
    await mutateFoodComponent({
      variables: {
        input: {
          id: foodComponent.id,
          isVerified,
          verifiedBy: user
        },
        kitchen: foodComponent.kitchen
      },
      onError: (e) => {
        toast.error(e.message);
      }
    }).catch(() => undefined);
  };

  const [isEditingActual, setIsEditingActual] = useState(false);
  const [newActualValue, setNewActualValue] = useState<string>('');
  const userActor = getUserAttributes(appContext.user);
  const [isEntriesOpen, setIsEntriesOpen] = useState(false);

  const actualCookedTotal = useMemo(() => {
    const entries = (foodComponent as any).actualCookedWeight as Array<{ quantity: number }> | undefined;
    if (!Array.isArray(entries)) return 0;
    return entries.reduce((sum, e) => sum + (Number(e?.quantity) || 0), 0);
  }, [foodComponent]);

  const lastEditableStationName = useMemo(() => getActualCookedWeightStation(foodComponent), [foodComponent]);

  const displayedCookedWeight = useMemo(() => {
    if (showAllUnits) {
      return round(
        ComponentService.calculateComponentQuantityFromWeight(
          [...(foodComponent.cups ?? [])],
          foodComponent.measurementUnit,
          foodComponent.cookedWeight ?? 0,
          weightChangeData.hasData
            ? round(weightChangeData.latestHourWeight, 2)
            : (round(foodComponent?.totalCookedWeight ?? 0, 2) ?? 0)
        ),
        6
      );
    }
    return weightChangeData.hasData
      ? round(weightChangeData.latestHourWeight, 2)
      : round(foodComponent?.totalCookedWeight ?? 0, 2);
  }, [showAllUnits, foodComponent, weightChangeData]);

  const adjustedCookedWeight = useMemo(() => {
    const buffers: ComponentWeightBuffer[] = appContext.kdsKitchen?.componentWeightBuffers ?? [];
    const baseCookedWeight = weightChangeData.hasData
      ? weightChangeData.latestHourWeight
      : (foodComponent?.totalCookedWeight ?? 0);
    const bufferPct = calculateBufferPercentage(baseCookedWeight, buffers);
    if (buffers.length > 0 && bufferPct > 0) {
      return round(baseCookedWeight * (1 + bufferPct / 100), 2);
    }
    return null;
  }, [appContext.kdsKitchen?.componentWeightBuffers, weightChangeData, foodComponent?.totalCookedWeight]);

  const expectedShortage = useMemo(() => {
    const base = adjustedCookedWeight ?? Number(displayedCookedWeight);
    if (actualCookedTotal === 0) return { value: '', percentage: '', isShortage: false, isWastage: false };
    const diff = base - actualCookedTotal;
    const pct = base > 0 ? round((Math.abs(diff) / base) * 100, 1) : 0;
    return {
      value: `${round(Math.abs(diff), 2)} g`,
      percentage: `${pct}%`,
      isShortage: diff > 0,
      isWastage: diff < 0
    };
  }, [adjustedCookedWeight, displayedCookedWeight, actualCookedTotal]);

  const handleSaveActualCooked = async () => {
    const quantity = Number(newActualValue);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Enter a valid positive number');
      return;
    }
    const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
    const nextArray = [
      ...existing.map((e) => ({
        quantity: Number(e.quantity),
        timestamp: e.timestamp,
        actor: e.actor as string,
        id: e.id as string
      })),
      { quantity, timestamp: new Date().toISOString(), actor: userActor.name }
    ];
    try {
      await updateActualCookedWeight({
        variables: {
          kitchen: foodComponent.kitchen,
          id: foodComponent.id,
          actualCookedWeight: nextArray
        }
      });
      setIsEditingActual(false);
      setNewActualValue('');
    } catch (error: any) {
      toast.error(error.message ?? 'Failed to update');
    }
  };

  const actionButtons = () => {
    if (combinedShortages) {
      return (
        <ActionButtons
          foodComponent={foodComponent}
          tableName={currentStation!.name}
          statusAttributes={statusAttributes}
          combinedShortages={combinedShortages}
          isChecked={isCheckedState}
          setIsCheckedState={setIsCheckedState}
        />
      );
    } else {
      return (
        <ActionButtons
          foodComponent={foodComponent}
          tableName={currentStation!.name}
          statusAttributes={statusAttributes}
          combinedShortages={undefined}
        />
      );
    }
  };

  const rowBg = selectedIdsSet.has(foodComponent.id)
    ? theme.palette.primary100
    : highlight
      ? 'rgba(255, 235, 59, 0.5)'
      : theme.palette.background.paper;

  const stickyCheckboxSx =
    hasCheckboxColumn && fixFirstTwoColumns
      ? {
          position: 'sticky' as const,
          left: 0,
          zIndex: 2,
          backgroundColor: rowBg,
          minWidth: STICKY_CHECKBOX_WIDTH,
          width: STICKY_CHECKBOX_WIDTH,
          boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
        }
      : {};

  const stickyNameSx = fixFirstTwoColumns
    ? {
        position: 'sticky' as const,
        left: hasCheckboxColumn ? STICKY_CHECKBOX_WIDTH : 0,
        zIndex: 2,
        backgroundColor: rowBg,
        minWidth: STICKY_NAME_WIDTH,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
      }
    : {};

  const stickyStationsSx =
    fixFirstTwoColumns && tableName === ('overview' as unknown as CookingStationName)
      ? {
          position: 'sticky' as const,
          left: (hasCheckboxColumn ? STICKY_CHECKBOX_WIDTH : 0) + STICKY_NAME_WIDTH,
          zIndex: 2,
          backgroundColor: rowBg,
          minWidth: STICKY_STATIONS_WIDTH,
          boxShadow: '4px 0 6px rgba(0,0,0,0.08)'
        }
      : {};

  return (
    <TableRow
      ref={rowRef}
      hover
      sx={{
        width: '100%',
        height: '62px',
        transition: 'background 0.5s',
        background: selectedIdsSet.has(foodComponent.id)
          ? theme.palette.primary100
          : highlight
            ? 'rgba(255, 235, 59, 0.5)'
            : undefined
      }}
    >
      {onComponentSelection && !combinedShortages && (
        <TableCell sx={{ width: '40px', px: '8px', ...stickyCheckboxSx }}>
          <Checkbox
            checked={selectedIdsSet.has(foodComponent.id)}
            onChange={(e) => onComponentSelection(foodComponent, e.target.checked)}
            sx={{ padding: 0 }}
          />
        </TableCell>
      )}
      <TableCell
        colSpan={onComponentSelection && !combinedShortages ? 2 : 3}
        sx={{ px: '15px', pr: 0, minWidth: '200px', ...stickyNameSx }}
      >
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Link
            style={{
              fontWeight: 600,
              fontSize: '16px',
              textDecoration: 'none',
              color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
            }}
            href={`/recipe-guide-book?id=${foodComponent.id.split('#')[1]}`}
          >
            {foodComponent.name.en}
          </Link>
          {foodComponent.isVerified && foodComponent.verifiedBy && isLastStation && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '14px',
                color: theme.palette.grey[500]
              }}
            >
              Verified by {foodComponent.verifiedBy.name}
            </Typography>
          )}

          {combinedShortages && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '19px',
                mt: '14px',
                color: theme.palette.neutral900
              }}
            >
              Quantity: {combinedShortages ? combinedShortages.totalWeight : 0} g
            </Typography>
          )}
        </Stack>
      </TableCell>
      {tableName === ('overview' as unknown as CookingStationName) && (
        <TableCell
          colSpan={2}
          padding="none"
          sx={{
            px: '10px',
            pb: 1,
            fontSize: '14px',
            textAlign: 'left',
            ...stickyStationsSx
          }}
        >
          {foodComponent.cookingStations?.length ? (
            foodComponent.cookingStations.map((station) =>
              currentStation?.name === station.name ? (
                <Tooltip key={station.id} title={`Current Station`} arrow placement="top">
                  <Typography
                    sx={{
                      fontWeight: '700',
                      textDecorationLine: 'underline',
                      color: theme.palette.black
                    }}
                  >
                    {FoodComponentStationTitle[station.name as keyof typeof FoodComponentStationTitle]}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  key={station.id}
                  sx={{
                    fontWeight: '500',
                    color: theme.palette.neutral600
                  }}
                >
                  {FoodComponentStationTitle[station.name as keyof typeof FoodComponentStationTitle]}
                </Typography>
              )
            )
          ) : (
            <Typography sx={{ fontSize: '16px', color: theme.palette.neutral400 }}>-</Typography>
          )}
        </TableCell>
      )}
      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 400,
          px: '10px',
          pb: 1,
          fontSize: '14px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        {weightChangeData.hasData
          ? round(weightChangeData.latestHourWeight * (foodComponent?.cookedRawFactor ?? 1), 2)
          : round((foodComponent?.totalCookedWeight ?? 0) * (foodComponent?.cookedRawFactor ?? 1), 2)}{' '}
        g
      </TableCell>

      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 400,
          px: '10px',
          pb: 1,
          fontSize: '14px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        {displayedCookedWeight} {showAllUnits ? foodComponent.measurementUnit : 'g'}
      </TableCell>

      {/* Buffer Adjusted Cooked Weight Column */}
      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 400,
          px: '10px',
          pb: 1,
          fontSize: '14px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        {adjustedCookedWeight === null ? '-' : `${adjustedCookedWeight} g`}
      </TableCell>

      {/* Weight Change Column */}
      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 600,
          px: '10px',
          pb: 1,
          fontSize: '16px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        {weightChangeData.hasData ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Tooltip title={`Compared from ${selectedHours}: ${round(weightChangeData.comparedWeight, 2)}g`} arrow>
              <Box
                sx={{
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  backgroundColor: weightChangeData.isIncrease
                    ? '#4CAF50'
                    : weightChangeData.isDecrease
                      ? '#F44336'
                      : theme.palette.black,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'help'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'white',
                    lineHeight: 1
                  }}
                >
                  {weightChangeData.isIncrease ? '↑' : weightChangeData.isDecrease ? '↓' : '−'}
                </Typography>
              </Box>
            </Tooltip>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
                }}
              >
                {round(weightChangeData.difference, 2)} g
              </Typography>
              {weightChangeData.comparedWeight > 0 && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: weightChangeData.isIncrease
                      ? '#4CAF50'
                      : weightChangeData.isDecrease
                        ? '#F44336'
                        : theme.palette.grey[500]
                  }}
                >
                  ({weightChangeData.isIncrease ? '+' : weightChangeData.isDecrease ? '-' : ''}
                  {round((weightChangeData.difference / weightChangeData.comparedWeight) * 100, 2)}%)
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.palette.neutral400
            }}
          >
            -
          </Typography>
        )}
      </TableCell>

      {/* Actual cooked weight (sum with inline add) */}
      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 600,
          px: '10px',
          pb: 1,
          fontSize: '16px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isEditingActual && currentStation?.name === lastEditableStationName ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >
              <TextField
                size="small"
                type="number"
                autoFocus
                value={newActualValue}
                onChange={(e) => setNewActualValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !updatingActual) {
                    e.preventDefault();
                    void handleSaveActualCooked();
                  } else if (e.key === 'Escape') {
                    setIsEditingActual(false);
                    setNewActualValue('');
                  }
                }}
                placeholder="Add g"
                sx={{ width: 110 }}
                slotProps={{
                  htmlInput: { min: 0, step: '0.01' }
                }}
              />
              <IconButton size="small" disabled={updatingActual} onClick={() => void handleSaveActualCooked()}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={updatingActual}
                onClick={() => {
                  setIsEditingActual(false);
                  setNewActualValue('');
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <>
              {currentStation?.name === lastEditableStationName ? (
                <>
                  <Typography sx={{ fontWeight: 600 }}>{round(actualCookedTotal, 2)} g</Typography>

                  <IconButton onClick={() => setIsEditingActual(true)} disabled={updatingActual}>
                    <EditIcon sx={{ color: theme.palette.black, fontSize: '22px' }} />
                  </IconButton>
                  <IconButton onClick={() => setIsEntriesOpen(true)} disabled={updatingActual}>
                    <ExpandMoreIcon sx={{ color: theme.palette.black, fontSize: '22px' }} />
                  </IconButton>
                </>
              ) : lastEditableStationName ? (
                <Typography sx={{ fontWeight: 400, fontSize: '14px', color: theme.palette.neutral800 }}>
                  Add in {FoodComponentStationTitle[lastEditableStationName as keyof typeof FoodComponentStationTitle]}
                </Typography>
              ) : (
                <></>
              )}
              {updatingActual && <CircularProgress size={16} thickness={5} />}
            </>
          )}
        </Stack>
      </TableCell>

      {/* Expected shortage/wastage */}
      <TableCell
        colSpan={2}
        padding="none"
        sx={{
          fontWeight: 400,
          px: '10px',
          pb: 1,
          fontSize: '14px',
          textAlign: 'center',
          color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
        }}
      >
        {expectedShortage.value && currentStation?.name === lastEditableStationName ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 400 }}>
              {expectedShortage.value} ({expectedShortage.percentage})
            </Typography>
            {expectedShortage.isShortage && (
              <Chip
                label="Shortage"
                size="small"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  fontSize: '13px',
                  height: '20px'
                }}
              />
            )}
            {expectedShortage.isWastage && (
              <Chip
                label="Wastage"
                size="small"
                sx={{
                  backgroundColor: '#ffc107',
                  color: theme.palette.common.black,
                  fontSize: '13px',
                  height: '20px'
                }}
              />
            )}
          </Stack>
        ) : (
          <Typography sx={{ fontWeight: 600 }}>-</Typography>
        )}
      </TableCell>

      <ActualCookedEntriesModal
        open={isEntriesOpen}
        onClose={() => setIsEntriesOpen(false)}
        entries={((foodComponent as any).actualCookedWeight as Array<any>) ?? []}
        canEdit={currentStation?.name === lastEditableStationName}
        isUpdating={updatingActual}
        onEdit={async (index, q) => {
          if (currentStation?.name !== lastEditableStationName) return;
          const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
          const nextArray = existing.map((e: any, i: number) => ({
            quantity: i === index ? Number(q) : Number(e.quantity),
            timestamp: e.timestamp,
            actor: e.actor as string,
            id: e.id as string
          }));
          await updateActualCookedWeight({
            variables: { kitchen: foodComponent.kitchen, id: foodComponent.id, actualCookedWeight: nextArray }
          });
        }}
        onDelete={async (index) => {
          if (currentStation?.name !== lastEditableStationName) return;
          const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
          const nextArray = existing
            .filter((_: any, i: number) => i !== index)
            .map((e: any) => ({
              quantity: Number(e.quantity),
              timestamp: e.timestamp,
              actor: e.actor as string,
              id: e.id as string
            }));
          await updateActualCookedWeight({
            variables: { kitchen: foodComponent.kitchen, id: foodComponent.id, actualCookedWeight: nextArray }
          });
        }}
      />

      {foodComponent.brand !== Brand.CALO && (
        <TableCell padding="none" sx={{ px: '10px', pb: 1, fontSize: '16px' }}>
          <BrandIndicator brand={foodComponent.brand ?? Brand.CALO} />
        </TableCell>
      )}

      <TableCell colSpan={3} padding="none" align="center">
        {statusAttributes.isCheckable || currentStation?.endTime ? (
          (currentStation && !currentStation.endTime) || combinedShortages ? (
            actionButtons()
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                textAlign: 'center',
                marginRight: '20px'
              }}
            >
              {isLastStation && (
                <>
                  {foodComponent.isVerified ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: '20px'
                      }}
                    >
                      <VerifiedIcon />
                      <Typography
                        sx={{
                          fontWeight: '500',
                          fontSize: '13px',
                          marginLeft: '5px'
                        }}
                      >
                        VERIFIED
                      </Typography>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      disabled={loading}
                      onClick={() => void handleFoodComponentVerify(true)}
                      startIcon={<VerifiedIcon />}
                      sx={{
                        color: theme.palette.black,
                        backgroundColor: theme.palette.white,
                        borderColor: theme.palette.black,
                        borderRadius: '5px',
                        textTransform: 'capitalize'
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: '500',
                          fontSize: '13px'
                        }}
                      >
                        VERIFY
                      </Typography>
                    </Button>
                  )}
                </>
              )}
              {foodComponent.isVerified && isLastStation ? (
                <Button
                  variant="outlined"
                  disabled={!statusAttributes.isCheckable || loading || appContext.isOffline}
                  onClick={() => setIsConfirmationModalOpened(true)}
                  style={{ width: '32px', minWidth: '32px' }}
                  sx={{
                    backgroundColor: theme.palette.white,
                    borderColor: theme.palette.black,
                    borderRadius: '5px',
                    padding: 0,
                    height: '32px',
                    '&:disabled': {
                      backgroundColor: theme.palette.white
                    }
                  }}
                >
                  <UndoIcon sx={{ color: theme.palette.black }} />
                </Button>
              ) : (
                <Typography
                  sx={{
                    color: theme.palette.black,
                    fontWeight: 700,
                    fontSize: '16px',
                    ml: '20px',
                    textAlign: 'center'
                  }}
                >
                  COMPLETED
                  {tableName === ('overview' as unknown as CookingStationName) && currentStation ? (
                    <Typography component="div" sx={{ color: theme.palette.black, fontSize: '14px' }}>
                      at{' '}
                      <Typography component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {FoodComponentStationTitle[currentStation.name as keyof typeof FoodComponentStationTitle]}
                      </Typography>
                    </Typography>
                  ) : (
                    <></>
                  )}
                </Typography>
              )}
            </Box>
          )
        ) : (
          <Typography sx={{ color: theme.palette.neutral600, fontWeight: 600, fontSize: '16px', textAlign: 'center' }}>
            Pending
          </Typography>
        )}
        {combinedShortages && (
          <Typography sx={{ color: theme.palette.red, fontWeight: 400, fontSize: '14px', lineHeight: '17px', mr: 3, mt: '14px' }}>
            Shortage
          </Typography>
        )}
      </TableCell>
      <Modal
        loading={loading}
        open={isConfirmationModalOpened}
        setOpen={(isOpened: boolean) => setIsConfirmationModalOpened(isOpened)}
        handleClose={() => setIsConfirmationModalOpened(false)}
        handleAccept={() => void handleFoodComponentVerify(false)}
        title={'Undo confirmation '}
        message={'Are you sure you want to undo this action?'}
      />
    </TableRow>
  );
};

export default memo(SectionTableRow);
